import type { DailyScore, MatchOutcome } from "@obean/game-contract";
import type { DailySource } from "@obean/game-contract/daily";
import { FIRST_DAILY, dailyOf, dailyScoreText, dailyTier } from "@obean/game-contract/daily";
import type { DailyResult } from "../net/api.js";

/**
 * Your best at today's daily (P4-TANK-03), kept in this browser per game and day: a win beats a
 * loss, then more stars, then a higher score. Signed in, each new best also goes to the server
 * for friends to see (`shareDaily`, privacy policy 1.8).
 */
export interface DailyBest {
  result: "win" | "loss";
  stars: number;
  score: number;
  /** The game's tier and score in words (ADR 0041), for a game with `dailyScore`. */
  tier?: number;
  told?: string;
}

const key = (gameId: string, day: string) => `obean.daily.${gameId}.${day}`;

export function bestOf(outcome: MatchOutcome, told?: DailyScore): DailyBest {
  return {
    result: outcome.result,
    stars: outcome.stats["stars"] ?? 0,
    score: outcome.stats["score"] ?? outcome.stats["right"] ?? outcome.stats["kills"] ?? 0,
    ...(told ? { tier: dailyTier(told, outcome), told: dailyScoreText(told, outcome.stats) } : {}),
  };
}

/** Whether `a` is better than `b`. */
export function better(a: DailyBest, b: DailyBest | null): boolean {
  if (!b) return true;
  if (a.result !== b.result) return a.result === "win";
  if (a.stars !== b.stars) return a.stars > b.stars;
  return a.score > b.score;
}

export function dailyBest(
  gameId: string,
  day: string,
  storage: Pick<Storage, "getItem"> = localStorage,
): DailyBest | null {
  try {
    const raw = storage.getItem(key(gameId, day));
    return raw ? (JSON.parse(raw) as DailyBest) : null;
  } catch {
    return null;
  }
}

/** Keeps a play of today's daily if it's your best; returns your best and whether it's new. */
export function recordDaily(
  gameId: string,
  day: string,
  outcome: MatchOutcome,
  storage: Pick<Storage, "getItem" | "setItem"> = localStorage,
  told?: DailyScore,
): { best: DailyBest; isNew: boolean } {
  const now = bestOf(outcome, told);
  const before = dailyBest(gameId, day, storage);
  if (!better(now, before)) return { best: before ?? now, isNew: false };
  try {
    storage.setItem(key(gameId, day), JSON.stringify(now));
  } catch {
    // No storage: the best lasts as long as the page.
  }
  return { best: now, isNew: true };
}

/** A best as the api takes it: whole numbers within its bounds. */
export function asResult(best: DailyBest): DailyResult {
  const whole = (n: number, max: number) => Math.min(max, Math.max(0, Math.round(n)));
  return {
    won: best.result === "win",
    stars: whole(best.stars, 3),
    score: whole(best.score, 1_000_000),
  };
}

/**
 * Signed in, a new best of the day goes to the server for friends to see (privacy policy 1.8);
 * guests keep it in this browser only. True when it was sent. Never throws: offline, it stays here.
 */
export async function shareDaily(
  gameId: string,
  day: string,
  best: DailyBest,
  client: {
    me(): Promise<{ signedIn?: boolean }>;
    saveDaily(gameId: string, day: string, result: DailyResult): Promise<unknown>;
  },
): Promise<boolean> {
  try {
    if ((await client.me()).signedIn !== true) return false;
    await client.saveDaily(gameId, day, asResult(best));
    return true;
  } catch {
    return false;
  }
}

const DAY_KEY = /^obean\.daily\.[a-z0-9-]+\.(\d{4}-\d{2}-\d{2})$/;

/** The days this browser played any game's daily, from the bests it keeps. */
export function playedDays(storage: Pick<Storage, "length" | "key"> = localStorage): Set<string> {
  const days = new Set<string>();
  try {
    for (let i = 0; i < storage.length; i++) {
      const day = DAY_KEY.exec(storage.key(i) ?? "")?.[1];
      if (day) days.add(day);
    }
  } catch {
    // No storage: no days.
  }
  return days;
}

/** Days a streak can rest (ADR 0043's guardrail): one day off a week, never two days running. */
const REST_EVERY = 7;

/**
 * The daily streak (ADR 0041): days in a row with a daily played, in any game. It's still alive
 * on a day not played yet, counted to yesterday. No punishment for a break (ADR 0043): one day off
 * a week keeps it going and counts as part of it; a second within the week, or two days running,
 * starts it again. A nudge, with no rewards or reminders.
 */
export function dailyStreak(days: ReadonlySet<string>, today: string): number {
  const at = Date.parse(`${today}T00:00:00Z`);
  if (!Number.isFinite(at)) return 0;
  const dayAt = (back: number) => new Date(at - back * 86_400_000).toISOString().slice(0, 10);
  let back = days.has(today) ? 0 : 1;
  let rested = -Infinity;
  for (; ; back++) {
    if (days.has(dayAt(back))) continue;
    // A day off between played days, the first in a week: rested, not broken.
    if (back - rested >= REST_EVERY && days.has(dayAt(back + 1))) rested = back;
    else break;
  }
  // From the first day counted (today, or yesterday when today isn't played yet).
  return back - (days.has(today) ? 0 : 1);
}

/** How many have played a daily today, in words (ADR 0043 part 5). */
export const playedTodayText = (n: number) =>
  `${n.toLocaleString("en")} ${n === 1 ? "person has" : "people have"} played today's daily so far.`;

/** A streak in words, from two days: "3 days in a row". */
export const streakText = (n: number) => (n >= 2 ? `${String(n)} days in a row` : "");

/** The most past dailies the archive lists (ADR 0041): about a month. */
export const ARCHIVE_DAYS = 30;

/**
 * A game's past dailies, newest first, from yesterday back to the first daily (at most
 * ARCHIVE_DAYS): each day's mission and seed, the same for everyone, playable again.
 */
export function pastDailies(
  game: DailySource,
  today: string,
): { day: string; missionId: string; seed: number }[] {
  const at = Date.parse(`${today}T00:00:00Z`);
  const first = Date.parse(`${FIRST_DAILY}T00:00:00Z`);
  if (!Number.isFinite(at)) return [];
  const out: { day: string; missionId: string; seed: number }[] = [];
  for (let back = 1; back <= ARCHIVE_DAYS; back++) {
    const ms = at - back * 86_400_000;
    if (ms < first) break;
    const d = dailyOf(game, new Date(ms).toISOString().slice(0, 10));
    if (d) out.push(d);
  }
  return out;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** A day as people say it: "Tue 3 Nov", the same in every browser (their own formats differ). */
export function dayName(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (!Number.isFinite(d.getTime())) return day;
  return `${WEEKDAYS[d.getUTCDay()] ?? ""} ${String(d.getUTCDate())} ${MONTHS[d.getUTCMonth()] ?? ""}`;
}

/** A play's daily on its events (docs/metrics.md): `daily` for today's, `archive` for a past one. */
export const dailyProps = (d: { day: string; past?: boolean } | undefined) =>
  d ? (d.past ? { archive: d.day } : { daily: d.day }) : {};
